import { EditOutlined } from '@ant-design/icons';
import { useMemoizedFn } from 'ahooks';
import { AutoComplete, Form, Input, Switch, Tooltip, Collapse } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import classNames from 'classnames';
import { CarrierTypeEnum } from 'domains/shippingDomain';
import { useOrgId } from 'hooks/useOrg';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RULE_INTEGER, RULE_TEXT_LEN_255 } from 'utils/validators';
import {
  ExpansionStateEnum,
  OrgShippingCarrier,
  OrgShippingCarrierFedEx,
} from 'domains/orgDomain';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxButton } from 'components/RevxButton';
import { allStatesOptions } from './CarriersFormUPS';

const { OPEN, CLOSED } = ExpansionStateEnum;

const COLLAPSE_OPEN_KEY = '1';

const CarriersFormFedEx = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [allowBtnDisabled, setAllowBtnDisabled] = useState(true);

  const carrierFedEx = useAppSelector((state) => {
    return state.shipping.orgShippingConfig?.carriers?.find(
      (c) => c.carrierType === CarrierTypeEnum.FedEx
    );
  });
  const fedEx = carrierFedEx?.fedEx;

  useEffect(() => {
    if (fedEx) {
      allowBtnDisabledHandler(fedEx);
    }
  }, [fedEx]);

  const loading = useAppSelector(
    (state) =>
      state.loading.effects.shipping.getOrgShippingConfig ||
      state.loading.effects.shipping.saveOrgShippingCarrier
  );
  const dispatch = useAppDispatch();
  const orgId = useOrgId();

  const [editing, setEditing] = useState<boolean>(false);
  const openProp = editing ? {} : { open: false };
  const [countryCode, setCountryCode] = useState(fedEx?.payer?.countryCode);
  const [stateOptions, setStateOptions] = useState(allStatesOptions);

  const onSearchStates = (searchText: string) => {
    if (countryCode === 'US') {
      if (!searchText) {
        setStateOptions(allStatesOptions);
      } else {
        setStateOptions(
          allStatesOptions.filter((o) => {
            if (searchText.length === 1) {
              return o.value.toLowerCase().startsWith(searchText.toLowerCase());
            } else if (searchText.length === 2) {
              return o.value.toLowerCase() === searchText.toLowerCase();
            }
            return o.label.toLowerCase().includes(searchText.toLowerCase());
          })
        );
      }
    }
  };
  const onSave = useMemoizedFn(async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (validationError) {
      return;
    }
    setSaveLoading(true);

    const shouldSkipAddrValidation =
      isEmpty(values.payer.city) ||
      isEmpty(values.payer.postalCode) ||
      isEmpty(values.payer.countryCode) ||
      isEmpty(values.payer.streetLines) ||
      isEmpty(values.payer.residential) ||
      isEmpty(values.payer.stateOrProvinceCode);

    values.payer.streetLines = [values.payer.streetLines];
    if (shouldSkipAddrValidation) {
      await updateCarrierHandler(values);
      setEditing(false);
    }
    setSaveLoading(false);
  });

  const updateCarrierHandler = async (
    values: Partial<OrgShippingCarrier | OrgShippingCarrierFedEx> & {
      allowed?: boolean;
    }
  ) => {
    const { allowed } = values;
    await dispatch.shipping.saveOrgShippingCarrier({
      orgId,
      ...({
        ...carrierFedEx,
        ...{
          allowed: allowed !== undefined ? allowed : carrierFedEx?.allowed,
        },
        ...{
          fedEx: {
            ...fedEx,
            ...values,
          },
        },
      } as OrgShippingCarrier),
    });
  };

  const onCollapseChange = async (keys: string | string[]) => {
    await updateCarrierHandler({
      expansionState: keys.length > 1 ? OPEN : CLOSED,
    });
  };

  const orgCarrierAllow = async (checked: boolean) => {
    form.setFieldsValue({ allowed: checked });
  };

  const allowBtnDisabledHandler = (payload: OrgShippingCarrierFedEx) => {
    const {
      clientSecret,
      clientId,
      accountNumber,
      payer: { countryCode },
    } = payload;
    setAllowBtnDisabled(
      !countryCode || !clientSecret || !clientId || !accountNumber
    );
  };

  return (
    <Collapse
      defaultActiveKey={fedEx?.expansionState === OPEN ? COLLAPSE_OPEN_KEY : 0}
      onChange={onCollapseChange}
    >
      <Collapse.Panel key={COLLAPSE_OPEN_KEY} header="FedEx">
        <RevxFlexLayout className="mb-40 allow-tips" justify="space-between">
          <RevxFlexLayout>
            <div className="mr-30">{`Allow ${carrierFedEx?.carrierType} for this organization?`}</div>
            <Switch
              disabled={!editing || allowBtnDisabled}
              onChange={orgCarrierAllow}
              defaultChecked={carrierFedEx?.allowed}
            />
          </RevxFlexLayout>
          {!editing && (
            <Tooltip title={t('common.edit')}>
              <EditOutlined
                onClick={() => {
                  setEditing(true);
                }}
              />
            </Tooltip>
          )}
        </RevxFlexLayout>
        <Form
          labelCol={{ span: 8 }}
          form={form}
          wrapperCol={{ span: 16 }}
          layout={'horizontal'}
          initialValues={{
            ...fedEx,
            ...{
              payer: {
                ...fedEx?.payer,
                streetLines: fedEx?.payer?.streetLines?.[0],
              },
              allowed: carrierFedEx?.allowed,
            },
          }}
          onValuesChange={(changedValues, allValues) => {
            allowBtnDisabledHandler(allValues);
          }}
          autoComplete="off"
        >
          <Form.Item name="allowed" hidden={true} />
          <Form.Item
            label={t('platform.shipping.form.clientId')}
            name="clientId"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input
              readOnly={!editing}
              className={classNames({
                'revx-input-no-border': !editing,
              })}
              autoComplete="off"
            />
          </Form.Item>
          <Form.Item
            label={t('platform.shipping.form.clientSecret')}
            name="clientSecret"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input
              readOnly={!editing}
              className={classNames({
                'revx-input-no-border': !editing,
              })}
              autoComplete="off"
            />
          </Form.Item>
          <Form.Item
            label={t('platform.shipping.form.accountNumber')}
            name="accountNumber"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input
              readOnly={!editing}
              className={classNames({
                'revx-input-no-border': !editing,
              })}
              autoComplete="off"
            />
          </Form.Item>
          <Form.Item
            label={t('platform.shipping.form.ltlShipperAccount')}
            name="ltlShipperAccount"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input
              readOnly={!editing}
              autoComplete="off"
              className={classNames({
                'revx-input-no-border': !editing,
              })}
            />
          </Form.Item>
          <Form.Item
            label={t('platform.shipping.form.ltlBillToAccount')}
            name="ltlBillToAccount"
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input
              readOnly={!editing}
              autoComplete="off"
              className={classNames({
                'revx-input-no-border': !editing,
              })}
            />
          </Form.Item>

          <Form.Item
            label={t('platform.shipping.form.freightEnabled')}
            name="freightEnabled"
            valuePropName="checked"
          >
            <Switch disabled={!editing} />
          </Form.Item>
          <Form.Item
            label={t('platform.shipping.form.packageEnabled')}
            name="packageEnabled"
            valuePropName="checked"
          >
            <Switch disabled={!editing} />
          </Form.Item>
          <Form.Item
            label={t('platform.shipping.form.markup')}
            name="markupPct"
            rules={[RULE_INTEGER, { required: false }]}
          >
            <Input
              readOnly={!editing}
              prefix={'%'}
              autoComplete="off"
              className={classNames({
                'revx-input-no-border': !editing,
              })}
            />
          </Form.Item>
          <Form.Item label={t('platform.shipping.form.payer')} />
          <Form.Item
            label={t('platform.shipping.form.countryCode')}
            name={['payer', 'countryCode']}
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <AutoComplete
              onChange={(val) => {
                setCountryCode(val);
                if (val === 'US') {
                  setStateOptions(allStatesOptions);
                } else {
                  setStateOptions([]);
                }
              }}
              options={[
                { label: 'US', value: 'US' },
                { label: 'CA', value: 'CA' },
              ]}
              {...openProp}
              className={classNames({
                'revx-input-no-border': !editing,
              })}
            />
          </Form.Item>
          <Form.Item
            label={t('platform.shipping.form.stateProvinceCode')}
            name={['payer', 'stateOrProvinceCode']}
          >
            <AutoComplete
              {...openProp}
              onSearch={onSearchStates}
              options={stateOptions}
              className={classNames({
                'revx-input-no-border': !editing,
              })}
            />
          </Form.Item>
          <Form.Item
            label={t('platform.shipping.form.city')}
            name={['payer', 'city']}
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input
              readOnly={!editing}
              autoComplete="off"
              className={classNames({
                'revx-input-no-border': !editing,
              })}
            />
          </Form.Item>
          <Form.Item
            label={t('platform.shipping.form.streetLine')}
            name={['payer', 'streetLines']}
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input
              readOnly={!editing}
              autoComplete="off"
              className={classNames({
                'revx-input-no-border': !editing,
              })}
            />
          </Form.Item>

          <Form.Item
            label={t('platform.shipping.form.postalCode')}
            name={['payer', 'postalCode']}
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input
              readOnly={!editing}
              autoComplete="off"
              className={classNames({
                'revx-input-no-border': !editing,
              })}
            />
          </Form.Item>
        </Form>
        {editing && (
          <RevxFlexLayout justify="justify-end">
            <RevxButton
              onClick={() => {
                setEditing(false);
                form.setFieldsValue({ ...fedEx });
              }}
            >
              {t('common.cancel')}
            </RevxButton>
            <RevxButton
              type="primary"
              className="ml-10"
              loading={saveLoading}
              onClick={() => {
                if (!loading && !saveLoading) {
                  onSave();
                }
              }}
            >
              {t('common.save')}
            </RevxButton>
          </RevxFlexLayout>
        )}
      </Collapse.Panel>
    </Collapse>
  );
};

export default CarriersFormFedEx;
